// Input de búsqueda con cruz para limpiar (y lupa opcional).
//
// POR QUÉ. Cada pantalla se montaba su propio <input> de buscar y casi ninguna dejaba vaciarlo de un
// toque: en el móvil, sin teclado físico ni tecla Esc, borrar lo escrito es incómodo. Este componente
// centraliza la lupa y la cruz para que todos los buscadores de la app se comporten igual.
//
// Solo aporta el input: el debounce, la navegación por teclado o el desplegable de cada pantalla se
// quedan donde estaban (se pasan como props sueltas: ref, onKeyDown, onFocus, autoFocus…).
import { forwardRef, useRef } from 'react'
import { Ico } from './ui'
// B-262 · QUE EL NAVEGADOR NO LE META AQUI EL CORREO DEL INICIO DE SESION. De esta caja cuelgan
// casi todos los buscadores de la aplicacion, asi que el remedio se pone UNA vez y llega a todos.
// El porque completo (y por que no basta con autoComplete="off"), en components/antiAutorrelleno.js.
import { antiAutorrelleno } from './antiAutorrelleno'
import { t, marca, tDato } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

const InputBusqueda = forwardRef(function InputBusqueda({
  value,
  onChange,
  onLimpiar,
  placeholder = marca('components.InputBusqueda.buscar', 'Buscar…'),
  style,
  contenedorStyle,
  cruzStyle,
  lupa = false,
  lupaSize = 15,
  lupaColor = 'var(--text-muted)',
  lupaLeft = 10,
  padLeft,
  padRight = 32,
  // Como se llama el campo para el navegador. Si no se dice, se saca del propio marcador de
  // posicion, del que ya sale un nombre distinto por pantalla («Buscar paciente…» -> busq-paciente).
  nombre,
  ...rest
}, refExterno) {
  const interno = useRef(null)

  const asignarRef = (nodo) => {
    interno.current = nodo
    if (typeof refExterno === 'function') refExterno(nodo)
    else if (refExterno) refExterno.current = nodo
  }

  const hayTexto = value != null && String(value) !== ''

  const limpiar = () => {
    const nodo = interno.current
    // Se finge el evento de un input ya vacío para que el onChange de quien nos usa (que casi siempre
    // lee e.target.value) funcione igual que al borrar a mano, sin obligar a pasar un callback extra.
    if (onChange) {
      if (nodo) nodo.value = ''
      const falso = nodo || { value: '' }
      onChange({ target: falso, currentTarget: falso, preventDefault() {}, stopPropagation() {} })
    }
    if (onLimpiar) onLimpiar()
    nodo?.focus()
  }

  // El padding derecho se reserva SIEMPRE (no solo cuando hay texto): así el texto nunca queda debajo
  // de la cruz y la caja no da un salto al aparecer o desaparecer el botón.
  const estiloInput = {
    boxSizing: 'border-box',
    ...style,
    ...(lupa ? { paddingLeft: padLeft ?? 34 } : null),
    paddingRight: padRight,
  }

  // El contenedor va sin minWidth propio: hay inputs que llevan el suyo (minWidth: 260) y si el
  // contenedor pudiera encoger más que ellos, el input se saldría y la cruz quedaría descolocada.
  return (
    <div style={{ position: 'relative', ...contenedorStyle }}>
      {lupa && (
        <span style={{ position: 'absolute', left: lupaLeft, top: '50%', transform: 'translateY(-50%)', display: 'flex', color: lupaColor, pointerEvents: 'none' }}>
          <Ico n="search" s={lupaSize} />
        </span>
      )}
      {/* Los atributos van los PRIMEROS, para que una pantalla que necesite su propio `name` o su
          propio `autoComplete` pueda seguir poniendolo encima con {...rest}. */}
      <input {...antiAutorrelleno(nombre || placeholder)}
        ref={asignarRef} value={value ?? ''} onChange={onChange} placeholder={tDato(placeholder)} style={estiloInput} {...rest} />
      {hayTexto && (
        <button type="button" onClick={limpiar}
          // Sin esto el input pierde el foco ANTES del click y los buscadores con desplegable
          // (que se cierran al perder el foco) se cerrarían justo al pulsar la cruz.
          onMouseDown={e => e.preventDefault()}
          title={t('components.InputBusqueda.limpiar_busqueda', 'Limpiar búsqueda')} aria-label={t('components.InputBusqueda.limpiar_busqueda', 'Limpiar búsqueda')}
          style={{
            position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)',
            width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 0, border: 'none', background: 'transparent', borderRadius: '50%',
            cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 0, ...cruzStyle,
          }}>
          <Ico n="x" s={15} />
        </button>
      )}
    </div>
  )
})

export default InputBusqueda
